import type { Scene } from '../../../types/typing.ts';

/**
 * Sliding scene-prefetch window: current scene + the next few, for linear
 * playback over a thin pipe.
 *
 * The anchor is the *playing* scene (derived from the staged background), not
 * the clicked selection — timeline auto-play advances scenes without ever
 * touching selection state, so a selection-anchored window goes stale and the
 * playhead runs into cold scenes. Pure positional logic with zero runtime
 * imports so it stays trivially unit-testable.
 */

/** How many scenes ahead of the anchor to prefetch (4-6 scenes/video avg). */
export const PREFETCH_AHEAD = 2;

export interface PrefetchItem {
  id: string;
  /** Canonical (SAS-stripped) key, or null for non-video backgrounds. */
  videoKey: string | null;
  /** Freshly signed playback URL, or null for non-video backgrounds. */
  videoUrl: string | null;
}

export interface PrefetchWindow {
  /** Every key the window owns (anchor + queue): stale-cancel spares these. */
  wanted: string[];
  /** Signed URLs to start warming, nearest first. */
  queue: string[];
}

/** Resolve scene backgrounds into prefetch items (caller's URL helpers in). */
/** Resolve scene backgrounds into prefetch items (caller's URL helpers in). */
export const toPrefetchItems = (
  scenes: Pick<Scene, 'id' | 'backgroundImage'>[],
  resolveUrl: (background: string) => string, // eslint-disable-line no-unused-vars
  isVideoUrl: (url: string) => boolean, // eslint-disable-line no-unused-vars
  canonicalKey: (url: string) => string // eslint-disable-line no-unused-vars
): PrefetchItem[] =>
  scenes.map((scene) => {
    const id = scene?.id ?? '';
    const background = scene?.backgroundImage;
    if (typeof background !== 'string' || !background) {
      return { id, videoKey: null, videoUrl: null };
    }
    const url = resolveUrl(background);
    if (!isVideoUrl(url)) return { id, videoKey: null, videoUrl: null };
    return { id, videoKey: canonicalKey(url), videoUrl: url };
  });

/**
 * Window over scene items: the anchor (staged/playing scene, else the
 * selected scene, else the list head) plus `count` videos ahead. Non-video
 * scenes are scanned over without consuming prefetch slots; scenes sharing
 * one blob collapse to a single queue entry.
 */
export const getPrefetchWindow = (
  items: PrefetchItem[],
  stagedKey: string | null,
  selectedId: string | null,
  count: number = PREFETCH_AHEAD
): PrefetchWindow => {
  let anchor =
    stagedKey !== null
      ? items.findIndex(
          (item) => item.videoKey !== null && item.videoKey === stagedKey
        )
      : -1;
  if (anchor < 0 && selectedId !== null) {
    anchor = items.findIndex((item) => item.id === selectedId);
  }
  const wanted: string[] = [];
  const queue: string[] = [];
  if (anchor >= 0 && items[anchor]?.videoKey) {
    wanted.push(items[anchor].videoKey as string);
  }
  const start = anchor >= 0 ? anchor + 1 : 0;
  for (let i = start; i < items.length && queue.length < count; i++) {
    const item = items[i];
    if (!item.videoKey || !item.videoUrl) continue;
    if (!wanted.includes(item.videoKey)) wanted.push(item.videoKey);
    if (!queue.some((queued) => queued === item.videoUrl)) {
      queue.push(item.videoUrl as string);
    }
  }
  return { wanted, queue };
};
